import { objectToSnake } from 'ts-case-convert';

import {
  createAiServerRequestHeaders,
  extractServerName,
  getConfiguredAiServerBaseUrl,
} from '@/api/ai/requestConfig';
import { ApiTokenStorage } from '@/module/auth/utils/ApiTokenStorage';
import { getClientFingerprintId } from '@/shared/utils/fingerprint';

import { type DirectGeneratePayloadDto } from './aiGenerateDockRequest';
import { isRecord } from './isRecord';
import { type PdfPageRange } from './pdfPageRanges';
import {
  type ServerAiBuilderChatConfirmDecision,
  type ServerAiBuilderChatErrorCode,
  type ServerAiBuilderChatEventDto,
  type ServerAiBuilderChatFeedbackResponseDto,
  type ServerAiBuilderChatHttpErrorDto,
  type ServerAiBuilderChatQuestionSnapshotDto,
  type ServerAiBuilderChatResetResponseDto,
  type ServerAiBuilderChatSessionResponseDto,
  type ServerAiBuilderChatSourceResponseDto,
  type ServerAiBuilderChatSourceType,
  type ServerAiBuilderChatUsageInfoDto,
  type ServerAiBuilderChatUsageState,
} from './serverAiBuilderChatDtos';
import { isServerAiBuilderChatErrorCode } from './serverAiBuilderChatErrorMessages';
import { parseServerAiBuilderChatSseStream } from './serverAiBuilderChatSseParser';
import { type AiBuilderTurnHints } from './types';

const SERVER_AI_BUILDER_CHAT_USAGE_STATES = [
  'normal',
  'low',
  'blocked',
] satisfies ServerAiBuilderChatUsageState[];

export type ServerAiBuilderChatFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export type ServerAiBuilderChatTransportDependencies = {
  baseUrl?: string;
  fetch?: ServerAiBuilderChatFetch;
  getToken?: () =>
    | string
    | null
    | undefined
    | Promise<string | null | undefined>;
  getServerName?: () =>
    | string
    | null
    | undefined
    | Promise<string | null | undefined>;
  getFingerprintId?: () =>
    | string
    | null
    | undefined
    | Promise<string | null | undefined>;
};

/** 생성기가 읽는 거절 세부 — 채팅 경로는 쓰지 않는다. */
export type ServerAiBuilderChatHttpErrorDetails = {
  usage?: ServerAiBuilderChatUsageInfoDto;
  retryAfterSeconds?: number;
  field?: string;
};

export class ServerAiBuilderChatHttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly code?: ServerAiBuilderChatErrorCode,
    public readonly reason?: string,
    public readonly details: ServerAiBuilderChatHttpErrorDetails = {},
  ) {
    super(code ?? `HTTP_${status}`);
    this.name = 'ServerAiBuilderChatHttpError';
  }
}

export const isServerAiBuilderChatHttpError = (
  value: unknown,
): value is ServerAiBuilderChatHttpError => {
  return value instanceof ServerAiBuilderChatHttpError;
};

export type CreateServerAiBuilderChatSessionRequest = {
  locale: string;
  /** 유저 국가 코드 (ISO 3166-1 alpha-2) — 서버 성취기준 검색 게이트 판정 축. */
  countryCode: string;
  draftInstanceId: string;
};

export type StreamServerAiBuilderChatTurnRequest = {
  sessionId: string;
  draftInstanceId: string;
  clientRequestId: string;
  message?: string;
  action?: 'chat' | 'undo';
  signal?: AbortSignal;
  confirm?: {
    token: string;
    decision: ServerAiBuilderChatConfirmDecision;
  } | null;
  sourceRefs?: string[];
  builderContext: {
    userName: string;
    schoolLevel: string;
    grade: string;
    subject: string;
  };
  selectedQuestionContext?: {
    id: string;
    number: number;
    question: string;
  } | null;
  turnHints?: AiBuilderTurnHints;
  /** 생성기 턴 — planner 없이 generate 1회. 있으면 message 를 보내지 않는다(서버가 만든다). */
  directGenerate?: DirectGeneratePayloadDto;
  questions: ServerAiBuilderChatQuestionSnapshotDto[];
  questionListRevision: string;
};

export type ResetServerAiBuilderChatSessionRequest = {
  sessionId: string;
  draftInstanceId: string;
};

export type UpdateServerAiBuilderChatFeedbackRequest = {
  sessionId: string;
  draftInstanceId: string;
  traceId: string;
  feedback: 'good' | 'bad' | null;
};

/** generator 면 서버가 추출 전에 생성기 사용량을 차감 없이 검사한다. */
export type ServerAiBuilderChatSourcePurpose = 'chat' | 'generator';

export type RegisterServerAiBuilderChatSourceRequest = (
  | {
      sessionId: string;
      draftInstanceId: string;
      sourceType: Extract<ServerAiBuilderChatSourceType, 'file'>;
      file: Blob;
      fileName?: string;
      pageRanges?: PdfPageRange[];
    }
  | {
      sessionId: string;
      draftInstanceId: string;
      sourceType: Exclude<ServerAiBuilderChatSourceType, 'file'>;
      url: string;
    }
) & {
  purpose?: ServerAiBuilderChatSourcePurpose;
  /** 생성기 중단이 업로드·추출 대기까지 끊도록 — 채팅 첨부는 넘기지 않는다. */
  signal?: AbortSignal;
};

export const createSession = async (
  request: CreateServerAiBuilderChatSessionRequest,
  dependencies: ServerAiBuilderChatTransportDependencies = {},
): Promise<ServerAiBuilderChatSessionResponseDto> => {
  const response = await getFetch(dependencies)(
    createUrl('/conversation/session', dependencies),
    {
      method: 'POST',
      credentials: 'include',
      headers: await createHeaders(dependencies, {
        accept: 'application/json',
        contentType: 'application/json',
      }),
      body: JSON.stringify({
        locale: request.locale,
        country_code: request.countryCode,
        draft_instance_id: request.draftInstanceId,
      }),
    },
  );

  return parseJsonResponse<ServerAiBuilderChatSessionResponseDto>(response);
};

/** 주입용 — 테스트·호출부가 같은 시그니처로 갈아 끼운다. */
export type StreamTurn = typeof streamTurn;

export const streamTurn = async (
  request: StreamServerAiBuilderChatTurnRequest,
  dependencies: ServerAiBuilderChatTransportDependencies = {},
): Promise<AsyncIterable<ServerAiBuilderChatEventDto>> => {
  const fetch = getFetch(dependencies);
  const url = createUrl('/conversation/turn', dependencies);
  const init: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: await createHeaders(dependencies, {
      accept: 'text/event-stream',
      contentType: 'application/json',
    }),
    signal: request.signal,
    body: JSON.stringify(createTurnRequestBody(request)),
  };

  const response = await fetchWithSinglePreResponseNetworkRetry(
    fetch,
    url,
    init,
    request.signal,
  );

  if (!response.ok) {
    return createHttpErrorEventStream(response);
  }

  if (!response.body) {
    throw new Error('Server AI builder chat turn response body is empty.');
  }

  return parseServerAiBuilderChatSseStream(response.body);
};

export const resetSession = async (
  request: ResetServerAiBuilderChatSessionRequest,
  dependencies: ServerAiBuilderChatTransportDependencies = {},
): Promise<ServerAiBuilderChatResetResponseDto> => {
  const response = await getFetch(dependencies)(
    createUrl(`/conversation/session/${request.sessionId}/reset`, dependencies),
    {
      method: 'POST',
      credentials: 'include',
      headers: await createHeaders(dependencies, {
        accept: 'application/json',
        contentType: 'application/json',
      }),
      body: JSON.stringify({
        draft_instance_id: request.draftInstanceId,
      }),
    },
  );

  return parseJsonResponse<ServerAiBuilderChatResetResponseDto>(response);
};

export const updateResponseFeedback = async (
  request: UpdateServerAiBuilderChatFeedbackRequest,
  dependencies: ServerAiBuilderChatTransportDependencies = {},
): Promise<void> => {
  const response = await getFetch(dependencies)(
    createUrl('/conversation/feedback', dependencies),
    {
      method: 'PUT',
      credentials: 'include',
      headers: await createHeaders(dependencies, {
        accept: 'application/json',
        contentType: 'application/json',
      }),
      body: JSON.stringify({
        session_id: request.sessionId,
        draft_instance_id: request.draftInstanceId,
        trace_id: request.traceId,
        feedback: request.feedback,
      }),
    },
  );

  await parseJsonResponse<ServerAiBuilderChatFeedbackResponseDto>(response);
};

/** 주입용 — 테스트·호출부가 같은 시그니처로 갈아 끼운다. */
export type RegisterSource = typeof registerSource;

export const registerSource = async (
  request: RegisterServerAiBuilderChatSourceRequest,
  dependencies: ServerAiBuilderChatTransportDependencies = {},
): Promise<ServerAiBuilderChatSourceResponseDto> => {
  const response = await getFetch(dependencies)(
    createUrl(
      `/conversation/session/${request.sessionId}/source`,
      dependencies,
    ),
    {
      method: 'POST',
      credentials: 'include',
      signal: request.signal,
      headers: await createHeaders(dependencies, {
        accept: 'application/json',
      }),
      body: createSourceFormData(request),
    },
  );

  return parseJsonResponse<ServerAiBuilderChatSourceResponseDto>(response);
};

const fetchWithSinglePreResponseNetworkRetry = async (
  fetch: ServerAiBuilderChatFetch,
  input: RequestInfo | URL,
  init: RequestInit,
  signal?: AbortSignal,
) => {
  try {
    return await fetch(input, init);
  } catch (error) {
    // 첫 SSE 응답이 열리기 전에 연결이 끊기는 일회성 Failed to fetch 가 관측됐다.
    // client_request_id replay protection이 있는 turn 요청만 한 번 재시도한다.
    if (!(error instanceof TypeError) || signal?.aborted) {
      throw error;
    }
    return fetch(input, init);
  }
};

const createTurnRequestBody = (
  request: StreamServerAiBuilderChatTurnRequest,
) => {
  const body = objectToSnake({
    sessionId: request.sessionId,
    draftInstanceId: request.draftInstanceId,
    clientRequestId: request.clientRequestId,
    ...(request.message !== undefined && !request.directGenerate
      ? { message: request.message }
      : {}),
    action: request.action ?? 'chat',
    confirm: request.confirm ?? null,
    sourceRefs: request.sourceRefs ?? [],
    builderContext: request.builderContext,
    ...(request.selectedQuestionContext
      ? { selectedQuestionContext: request.selectedQuestionContext }
      : {}),
    ...(request.turnHints ? { turnHints: request.turnHints } : {}),
    questionListRevision: request.questionListRevision,
  });

  return {
    ...body,
    // 이미 wire(snake_case) 형태라 변환 밖에 둔다.
    ...(request.directGenerate
      ? { direct_generate: request.directGenerate }
      : {}),
    questions: request.questions,
  };
};

const createSourceFormData = (
  request: RegisterServerAiBuilderChatSourceRequest,
) => {
  const formData = new FormData();
  formData.append('source_type', request.sourceType);
  formData.append('draft_instance_id', request.draftInstanceId);
  if (request.purpose === 'generator') {
    formData.append('purpose', request.purpose);
  }

  if (request.sourceType === 'file') {
    if (request.fileName) {
      formData.append('file', request.file, request.fileName);
    } else {
      formData.append('file', request.file);
    }
    if (request.pageRanges) {
      formData.append('page_ranges', JSON.stringify(request.pageRanges));
    }
    return formData;
  }

  formData.append('url', request.url);
  return formData;
};

const parseJsonResponse = async <ResponseDto>(
  response: Response,
): Promise<ResponseDto> => {
  if (!response.ok) {
    throw await createHttpError(response);
  }

  return (await response.json()) as ResponseDto;
};

async function* createHttpErrorEventStream(
  response: Response,
): AsyncIterable<ServerAiBuilderChatEventDto> {
  yield await createHttpErrorEvent(response);
}

const createHttpErrorEvent = async (
  response: Response,
): Promise<ServerAiBuilderChatEventDto> => {
  const parsedError = await parseHttpErrorDto(response);

  if (parsedError) {
    return {
      type: 'error',
      code: parsedError.code,
      message: parsedError.message,
      ...(parsedError.reason ? { reason: parsedError.reason } : {}),
      ...(parsedError.usage ? { usage: parsedError.usage } : {}),
      ...(parsedError.retry_after_seconds !== undefined
        ? { retry_after_seconds: parsedError.retry_after_seconds }
        : {}),
      ...(parsedError.field ? { field: parsedError.field } : {}),
    };
  }

  return createFallbackHttpErrorEvent();
};

const parseHttpErrorDto = async (
  response: Response,
): Promise<ServerAiBuilderChatHttpErrorDto | null> => {
  try {
    const body = (await response.json()) as unknown;
    const candidate = getHttpErrorCandidate(body);
    const parsed = normalizeServerAiBuilderChatHttpErrorDto(candidate);
    if (!parsed || parsed.retry_after_seconds !== undefined) return parsed;

    const retryAfterSeconds = parseRetryAfterHeader(response);
    return retryAfterSeconds === undefined
      ? parsed
      : { ...parsed, retry_after_seconds: retryAfterSeconds };
  } catch {
    // Invalid or empty error bodies fall back to a generic error code.
  }

  return null;
};

const parseRetryAfterHeader = (response: Response): number | undefined => {
  const value = Number(response.headers.get('Retry-After'));
  return Number.isFinite(value) && value > 0 ? value : undefined;
};

const getHttpErrorCandidate = (body: unknown): unknown => {
  if (!isRecord(body) || !('detail' in body)) return body;

  return body['detail'];
};

const createFallbackHttpErrorEvent = (): ServerAiBuilderChatEventDto => ({
  type: 'error',
  code: 'INTERNAL_ERROR',
  message: 'INTERNAL_ERROR',
});

const normalizeServerAiBuilderChatHttpErrorDto = (
  value: unknown,
): ServerAiBuilderChatHttpErrorDto | null => {
  if (isServerAiBuilderChatErrorCode(value)) {
    return {
      code: value,
      message: value,
    };
  }

  if (!isRecord(value)) return null;
  if (!isServerAiBuilderChatErrorCode(value['code'])) return null;

  if (
    value['usage'] !== undefined &&
    !isServerAiBuilderChatUsageInfoDto(value['usage'])
  ) {
    return null;
  }

  return {
    code: value['code'],
    message:
      typeof value['message'] === 'string' ? value['message'] : value['code'],
    ...(typeof value['reason'] === 'string' && value['reason']
      ? { reason: value['reason'] }
      : {}),
    ...(value['usage'] ? { usage: value['usage'] } : {}),
    ...(typeof value['retry_after_seconds'] === 'number'
      ? { retry_after_seconds: value['retry_after_seconds'] }
      : {}),
    ...(typeof value['field'] === 'string' && value['field']
      ? { field: value['field'] }
      : {}),
  };
};

const isServerAiBuilderChatUsageInfoDto = (
  value: unknown,
): value is ServerAiBuilderChatUsageInfoDto => {
  if (!isRecord(value)) return false;

  return (
    isServerAiBuilderChatUsageState(value['state']) &&
    typeof value['remaining'] === 'number' &&
    typeof value['limit'] === 'number'
  );
};

const isServerAiBuilderChatUsageState = (
  value: unknown,
): value is ServerAiBuilderChatUsageState => {
  return (
    typeof value === 'string' &&
    SERVER_AI_BUILDER_CHAT_USAGE_STATES.includes(
      value as ServerAiBuilderChatUsageState,
    )
  );
};

const createHeaders = async (
  dependencies: ServerAiBuilderChatTransportDependencies,
  options: {
    accept?: string;
    contentType?: string;
  },
) => {
  const headers = new Headers();

  if (options.accept) {
    headers.set('Accept', options.accept);
  }

  if (options.contentType) {
    headers.set('Content-Type', options.contentType);
  }

  const token = await resolveDependencyValue(
    dependencies.getToken,
    ApiTokenStorage.get,
  );
  const serverName = await resolveDependencyValue(
    dependencies.getServerName,
    extractServerName,
  );

  let fingerprintId: string | null | undefined;
  try {
    fingerprintId = await resolveDependencyValue(
      dependencies.getFingerprintId,
      getClientFingerprintId,
    );
  } catch {
    // Fingerprint is best-effort and must not block the request.
  }

  const requestHeaders = createAiServerRequestHeaders({
    token,
    serverName,
    fingerprintId,
  });

  for (const [key, value] of Object.entries(requestHeaders)) {
    headers.set(key, value);
  }

  return headers;
};

const resolveDependencyValue = async <Value>(
  provider: (() => Value | Promise<Value>) | undefined,
  fallback: () => Value | Promise<Value>,
) => {
  if (provider) {
    return provider();
  }

  return fallback();
};

const getFetch = (
  dependencies: ServerAiBuilderChatTransportDependencies,
): ServerAiBuilderChatFetch => {
  if (dependencies.fetch) return dependencies.fetch;

  if (typeof globalThis.fetch !== 'function') {
    throw new Error('fetch is not available for server AI builder chat.');
  }

  return globalThis.fetch.bind(globalThis);
};

const createUrl = (
  path: string,
  dependencies: ServerAiBuilderChatTransportDependencies,
) => {
  const baseUrl = dependencies.baseUrl ?? getConfiguredAiServerBaseUrl();

  if (!baseUrl) {
    throw new Error(
      'NEXT_PUBLIC_AI_SERVER_BASEURL is required for server AI builder chat.',
    );
  }

  return new URL(path, baseUrl).toString();
};

/**
 * JSON 경로(세션 생성·리셋·자료 등록·피드백)의 오류를 SSE 경로와 같은 계약으로 만든다.
 *
 * 종전에는 status 문자열만 담은 plain `Error` 를 던져 서버가 보낸 `detail.code` 가
 * 호출부에 도달하지 못했다. 그 결과 토큰 만료(401 AUTH_EXPIRED)와 일반 실패를
 * 구분할 수 없었다. `parseHttpErrorDto` 는 SSE 경로가 이미 쓰는 파서라, 두 채널이
 * 같은 정규화를 공유하게 된다.
 */
const createHttpError = async (response: Response) => {
  const parsedError = await parseHttpErrorDto(response);

  return new ServerAiBuilderChatHttpError(
    response.status,
    parsedError?.code,
    parsedError?.reason,
    {
      ...(parsedError?.usage ? { usage: parsedError.usage } : {}),
      ...(parsedError?.retry_after_seconds !== undefined
        ? { retryAfterSeconds: parsedError.retry_after_seconds }
        : {}),
      ...(parsedError?.field ? { field: parsedError.field } : {}),
    },
  );
};
